import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { Eye, EyeOff, AlertCircle, ShieldCheck } from 'lucide-react';
import api from '../services/api';

const LOGO_URL = "/logo.jpg";

// Crypto Matrix Rain Background Component
const CryptoMatrixRain = () => {
    const canvasRef = useRef(null);
    
    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        
        // Set canvas size
        const resizeCanvas = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };
        resizeCanvas();
        window.addEventListener('resize', resizeCanvas);
        
        // Crypto symbols and icons
        const cryptoSymbols = ['₿', 'Ξ', '₮', '$', '◎', '₳', '⟠', 'Ł', '◈', '✧', '⬡', '⬢', '◆', '●', '■'];
        const fontSize = 16;
        const columns = Math.floor(canvas.width / fontSize);
        
        // Array to track position of each column
        const drops = Array(columns).fill(1);
        const speeds = Array(columns).fill(0).map(() => Math.random() * 0.5 + 0.5);
        const symbols = Array(columns).fill('').map(() => 
            cryptoSymbols[Math.floor(Math.random() * cryptoSymbols.length)]
        );
        
        // Colors for different symbols
        const colors = {
            '₿': '#F7931A',  // Bitcoin orange
            'Ξ': '#627EEA',  // Ethereum purple
            '₮': '#26A17B',  // Tether green
            '$': '#2775CA',  // USDC blue
            '◎': '#9945FF',  // Solana purple
            '₳': '#0033AD',  // Cardano blue
            '⟠': '#E84142',  // Avalanche red
            'Ł': '#345D9D',  // Litecoin blue
            'default': '#5BA4A4'  // Cybernetics teal
        };
        
        const draw = () => {
            // Semi-transparent black for fade effect
            ctx.fillStyle = 'rgba(5, 5, 5, 0.05)';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            
            ctx.font = `${fontSize}px monospace`;
            
            for (let i = 0; i < drops.length; i++) {
                // Change symbol occasionally
                if (Math.random() > 0.98) {
                    symbols[i] = cryptoSymbols[Math.floor(Math.random() * cryptoSymbols.length)];
                }
                
                const symbol = symbols[i];
                const color = colors[symbol] || colors.default;
                
                // Draw with glow effect for some symbols
                if (Math.random() > 0.9) {
                    ctx.shadowBlur = 10;
                    ctx.shadowColor = color;
                } else {
                    ctx.shadowBlur = 0;
                }
                
                // Varying opacity based on position
                const opacity = Math.min(drops[i] * fontSize / canvas.height, 0.8);
                ctx.fillStyle = color + Math.floor(opacity * 255).toString(16).padStart(2, '0');
                
                ctx.fillText(symbol, i * fontSize, drops[i] * fontSize);
                
                // Reset when off screen or randomly for variation
                if (drops[i] * fontSize > canvas.height && Math.random() > 0.975) {
                    drops[i] = 0;
                    speeds[i] = Math.random() * 0.5 + 0.5;
                }
                
                drops[i] += speeds[i];
            }
        };
        
        const interval = setInterval(draw, 33);
        
        return () => {
            clearInterval(interval);
            window.removeEventListener('resize', resizeCanvas);
        };
    }, []);
    
    return (
        <canvas 
            ref={canvasRef} 
            className="fixed inset-0 z-0"
            style={{ background: 'linear-gradient(180deg, #050505 0%, #0a0a0a 100%)' }}
        />
    );
};

export default function LoginPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [requires2FA, setRequires2FA] = useState(false);
    const [userId, setUserId] = useState('');
    const [twoFactorCode, setTwoFactorCode] = useState('');
    const { login, setTokenAndUser } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const result = await login(email, password, 'trading');
            
            if (result?.requires_2fa) {
                setRequires2FA(true);
                setUserId(result.user_id);
            } else {
                navigate('/dashboard');
            }
        } catch (err) {
            setError(err.response?.data?.detail || 'Login failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleVerify2FA = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const response = await api.post('/auth/login/verify-2fa', {
                user_id: userId,
                code: twoFactorCode
            });
            
            if (response.data.access_token) {
                setTokenAndUser(response.data.access_token, response.data.user);
                navigate('/dashboard');
            }
        } catch (err) {
            setError(err.response?.data?.detail || 'Invalid verification code');
        } finally {
            setLoading(false);
        }
    };

    const handleBackToLogin = () => {
        setRequires2FA(false);
        setUserId('');
        setTwoFactorCode('');
        setError('');
    };

    return (
        <div className="min-h-screen relative overflow-hidden overflow-x-hidden">
            {/* Matrix Rain Background */}
            <CryptoMatrixRain />
            
            {/* Overlay gradient */}
            <div className="fixed inset-0 z-1 bg-gradient-to-b from-transparent via-black/30 to-black/60 pointer-events-none" />
            
            {/* Login Card */}
            <div className="relative z-10 min-h-screen flex items-center justify-center p-3 md:p-4">
                <motion.div 
                    className="w-full max-w-md bg-[#0a0a0a]/90 backdrop-blur-xl border border-[rgba(91,164,164,0.3)] rounded-2xl p-8 shadow-2xl"
                    initial={{ opacity: 0, y: 20, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.5 }}
                    style={{ boxShadow: '0 0 60px rgba(91,164,164,0.15)' }}
                >
                    {/* Logo */}
                    <motion.div 
                        className="flex flex-col items-center mb-8"
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                    >
                        <div className="relative mb-4">
                            <div className="absolute inset-0 bg-[#5BA4A4] blur-2xl opacity-30 rounded-full scale-150" />
                            <img 
                                src={LOGO_URL} 
                                alt="The Cybernetics" 
                                className="relative w-20 h-20 rounded-xl border border-[rgba(91,164,164,0.3)]"
                            />
                        </div>
                        <h1 className="text-2xl font-bold text-[#5BA4A4] uppercase tracking-wider">The Cybernetics</h1>
                        <p className="text-xs text-gray-500 mt-1">Secure Crypto Trading Platform</p>
                    </motion.div>

                    {error && (
                        <motion.div 
                            className="flex items-center gap-2 p-3 mb-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm"
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            data-testid="login-error"
                        >
                            <AlertCircle size={16} />
                            {error}
                        </motion.div>
                    )}

                    {requires2FA ? (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                        >
                            <div className="flex items-center gap-3 mb-4 p-4 bg-[#5BA4A4]/10 border border-[#5BA4A4]/30 rounded-lg">
                                <ShieldCheck size={24} className="text-[#5BA4A4]" />
                                <div>
                                    <p className="text-white font-medium">2FA Verification Required</p>
                                    <p className="text-xs text-gray-400">Enter the code sent to your email</p>
                                </div>
                            </div>

                            <form onSubmit={handleVerify2FA}>
                                <div className="mb-4">
                                    <label className="block text-xs text-gray-400 uppercase tracking-wider mb-2" htmlFor="2fa-code">
                                        Verification Code
                                    </label>
                                    <input
                                        id="2fa-code"
                                        type="text"
                                        className="w-full px-4 py-3 bg-black/50 border border-[rgba(91,164,164,0.3)] rounded-lg text-center text-2xl tracking-[0.5em] font-mono text-white focus:border-[#5BA4A4] outline-none transition-colors"
                                        placeholder="000000"
                                        value={twoFactorCode}
                                        onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                        maxLength={6}
                                        required
                                        autoFocus
                                        data-testid="2fa-code-input"
                                    />
                                </div>

                                <button
                                    type="submit"
                                    className="w-full py-3 bg-[#5BA4A4] text-black font-bold rounded-lg hover:bg-[#4a9393] transition-colors disabled:opacity-50"
                                    disabled={loading || twoFactorCode.length !== 6}
                                    data-testid="verify-2fa-btn"
                                >
                                    {loading ? 'Verifying...' : 'Verify & Login'}
                                </button>

                                <button
                                    type="button"
                                    onClick={handleBackToLogin}
                                    className="w-full mt-3 py-2 text-sm text-gray-400 hover:text-white transition-colors"
                                >
                                    ← Back to Login
                                </button>
                            </form>
                        </motion.div>
                    ) : (
                        <form onSubmit={handleSubmit}>
                            <div className="mb-4">
                                <label className="block text-xs text-gray-400 uppercase tracking-wider mb-2" htmlFor="email">
                                    Email
                                </label>
                                <input
                                    id="email"
                                    type="email"
                                    className="w-full px-4 py-3 bg-black/50 border border-[rgba(91,164,164,0.3)] rounded-lg text-white placeholder-gray-600 focus:border-[#5BA4A4] outline-none transition-colors"
                                    placeholder="Enter your email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    data-testid="login-email-input"
                                />
                            </div>

                            <div className="mb-6">
                                <label className="block text-xs text-gray-400 uppercase tracking-wider mb-2" htmlFor="password">
                                    Password
                                </label>
                                <div className="relative">
                                    <input
                                        id="password"
                                        type={showPassword ? 'text' : 'password'}
                                        className="w-full px-4 py-3 bg-black/50 border border-[rgba(91,164,164,0.3)] rounded-lg text-white placeholder-gray-600 focus:border-[#5BA4A4] outline-none transition-colors pr-12"
                                        placeholder="Enter your password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        data-testid="login-password-input"
                                    />
                                    <button
                                        type="button"
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 p-1"
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                </div>
                            </div>

                            <button
                                type="submit"
                                className="w-full py-3 bg-[#5BA4A4] text-black font-bold rounded-lg hover:bg-[#4a9393] transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:transform-none"
                                disabled={loading}
                                data-testid="login-submit-btn"
                            >
                                {loading ? (
                                    <span className="flex items-center justify-center gap-2">
                                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                                        </svg>
                                        Signing in...
                                    </span>
                                ) : 'Sign In'}
                            </button>
                        </form>
                    )}

                    {!requires2FA && (
                        <div className="mt-6 text-center text-sm text-gray-500">
                            Don't have an account?{' '}
                            <Link to="/register" className="text-[#5BA4A4] hover:underline" data-testid="register-link">
                                Create one
                            </Link>
                            <span className="mx-2 text-gray-700">·</span>
                            <Link to="/portal?mode=login" className="text-amber-400 hover:underline" data-testid="login-change-portal">Recovery Wallet?</Link>
                        </div>
                    )}
                    
                    {/* Decorative elements */}
                    <div className="absolute -top-20 -right-20 w-40 h-40 bg-[#5BA4A4]/5 rounded-full blur-3xl pointer-events-none" />
                    <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-[#F7931A]/5 rounded-full blur-3xl pointer-events-none" />
                </motion.div>
            </div>
        </div>
    );
}
